import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../App';
import api, { paymentAPI } from '../api/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

export default function BillingPage() {
  const { token: authToken } = useAuth();
  const token = authToken || localStorage.getItem('starfuel_token');
  const [searchParams] = useSearchParams();
  const selectedPlan = searchParams.get('plan'); // carried from /pricing CTA
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setupLoading, setSetupLoading] = useState(false);
  const [cardMounted, setCardMounted] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [upgrading, setUpgrading] = useState('');
  const [portalLoading, setPortalLoading] = useState(false);

  async function openPortal() {
    setPortalLoading(true);
    try {
      const res = await paymentAPI.openBillingPortal();
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        setPortalLoading(false);
      }
    } catch (e) {
      setPortalLoading(false);
    }
  }

  async function upgradeTo(planKey) {
    setUpgrading(planKey); setError('');
    try {
      const res = await paymentAPI.createCheckout(planKey);
      if (res.data?.url) {
        window.location.href = res.data.url; // go to Stripe Checkout
      } else {
        setError('Could not start checkout.');
        setUpgrading('');
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Could not start checkout.');
      setUpgrading('');
    }
  }

  useEffect(() => {
    api.get('/billing/status').then(r => { setStatus(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [token]);

  async function handleSetupCard() {
    setSetupLoading(true); setError('');
    try {
      const res = await api.post('/billing/setup').catch(e => { setError(e.response?.data?.error || 'Setup failed'); return null; });
      if (!res) return;
      const data = res.data;
      if (!window.Stripe) {
        await new Promise((resolve, reject) => {
          const s = document.createElement('script'); s.src = 'https://js.stripe.com/v3/';
          s.onload = resolve; s.onerror = reject; document.head.appendChild(s);
        });
      }
      const stripe = window.Stripe(STRIPE_PK);
      const elements = stripe.elements({ clientSecret: data.clientSecret });
      const cardElement = elements.create('card', { style: { base: { color: '#f0ede6', fontFamily: 'sans-serif', fontSize: '16px', '::placeholder': { color: '#666' } } } });
      setCardMounted(true);
      setTimeout(() => { const el = document.getElementById('card-element'); if (el) { el.innerHTML = ''; cardElement.mount('#card-element'); } else { console.error('card-element not found'); } }, 500);
      window._stripeSubmit = async () => {
        setSetupLoading(true);
        const { setupIntent, error: se } = await stripe.confirmCardSetup(data.clientSecret, { payment_method: { card: cardElement } });
        if (se) { setError(se.message); setSetupLoading(false); return; }
        const cr = await api.post('/billing/confirm-setup', { paymentMethodId: setupIntent.payment_method }).catch(() => null);
        if (cr) { setMessage('Card saved successfully!'); setCardMounted(false); const ns = await api.get('/billing/status'); setStatus(ns.data); }
        else setError('Failed to save card.');
        setSetupLoading(false);
      };
    } catch (err) { setError('Something went wrong.'); } finally { setSetupLoading(false); }
  }

  const PLANS = [
    { key: 'starter', name: 'Starter', price: '$8/delivery', desc: 'Pay as you go', color: 'border-gray-700' },
    { key: 'business', name: 'Business', price: '$199/mo', desc: '30 deliveries included', color: 'border-yellow-400' },
    { key: 'enterprise', name: 'Enterprise', price: '$899/mo', desc: '150 deliveries included', color: 'border-green-400/50' },
  ];

  if (loading) return <div className="min-h-screen bg-black flex items-center justify-center"><div className="animate-spin w-8 h-8 border-2 border-yellow-400 border-t-transparent rounded-full" /></div>;

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-gray-800 px-4 md:px-8 py-4 flex items-center gap-3">
        <Link to="/dashboard" className="text-gray-400 hover:text-white transition">← Dashboard</Link>
        <span className="text-gray-600">|</span>
        <h1 className="font-bold">Billing & Payments</h1>
      </header>
      <main className="px-4 md:px-8 py-8 max-w-3xl mx-auto space-y-6">
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
          <h2 className="font-semibold mb-4 flex items-center gap-2"><span>💳</span> Payment Method</h2>
          {status?.paymentSetup && status?.paymentMethod ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-8 bg-gray-800 rounded flex items-center justify-center text-xs text-gray-400 uppercase font-bold">{status.paymentMethod.brand}</div>
                <div>
                  <p className="text-white font-medium">•••• •••• •••• {status.paymentMethod.last4}</p>
                  <p className="text-gray-400 text-sm">Expires {status.paymentMethod.expMonth}/{status.paymentMethod.expYear}</p>
                </div>
              </div>
              <button onClick={handleSetupCard} className="text-sm text-yellow-400 hover:underline">Update card</button>
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-3 p-4 bg-yellow-400/10 border border-yellow-400/30 rounded-xl mb-4">
                <span className="text-2xl">⚠️</span>
                <div><p className="text-yellow-400 font-medium text-sm">No payment method on file</p><p className="text-gray-400 text-xs mt-0.5">Add a card to pay for deliveries automatically</p></div>
              </div>
              <button onClick={handleSetupCard} disabled={setupLoading} className="px-6 py-3 bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-xl transition disabled:opacity-50">
                {setupLoading ? 'Loading...' : '+ Add Payment Method'}
              </button>
            </div>
          )}
          {cardMounted && (
            <div className="mt-5">
              <div id="card-element" className="p-4 bg-black border border-gray-700 rounded-xl mb-4" />
              {error && <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 text-red-400 text-sm mb-4">{error}</div>}
              <div className="flex gap-3">
                <button onClick={() => setCardMounted(false)} className="flex-1 py-3 border border-gray-700 text-gray-400 rounded-xl hover:border-gray-500 transition">Cancel</button>
                <button onClick={() => window._stripeSubmit && window._stripeSubmit()} disabled={setupLoading} className="flex-1 py-3 bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-xl transition disabled:opacity-50">
                  {setupLoading ? 'Saving...' : 'Save Card'}
                </button>
              </div>
            </div>
          )}
          {message && <div className="mt-4 bg-green-500/10 border border-green-500/30 rounded-xl px-4 py-3 text-green-400 text-sm">✅ {message}</div>}
        </div>

        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
          <h2 className="font-semibold mb-4 flex items-center gap-2"><span>📋</span> Current Plan</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {PLANS.map(plan => (
              <div key={plan.key} className={`border ${plan.color} rounded-xl p-4 ${(status?.activePlan === plan.key || (!status?.activePlan && plan.key === 'starter')) ? 'bg-yellow-400/5' : ''} ${selectedPlan === plan.key && status?.activePlan !== plan.key ? 'ring-2 ring-yellow-400' : ''}`}>
                {selectedPlan === plan.key && status?.activePlan !== plan.key && (
                  <p className="text-yellow-400 text-xs font-bold mb-1">You selected this →</p>
                )}
                <div className="flex items-center justify-between mb-2">
                  <p className="font-semibold text-sm text-white">{plan.name}</p>
                  {(status?.activePlan === plan.key || (!status?.activePlan && plan.key === 'starter')) && <span className="text-xs bg-yellow-400 text-black px-2 py-0.5 rounded-full font-bold">Active</span>}
                </div>
                <p className="text-yellow-400 font-bold">{plan.price}</p>
                <p className="text-gray-400 text-xs mt-1">{plan.desc}</p>
                {plan.key !== 'starter' && status?.activePlan !== plan.key && (
                  <button onClick={() => upgradeTo(plan.key)} disabled={!!upgrading}
                    className="mt-3 w-full py-2 bg-yellow-400 hover:bg-yellow-300 text-black text-sm font-bold rounded-lg transition disabled:opacity-50">
                    {upgrading === plan.key ? 'Starting…' : `Upgrade to ${plan.name}`}
                  </button>
                )}
              </div>
            ))}
          </div>
          {status?.deliveriesRemaining > 0 && <p className="text-sm text-gray-400 mt-3">{status.deliveriesRemaining} deliveries remaining this month</p>}
          {(status?.activePlan === 'business' || status?.activePlan === 'enterprise') && (
            <button onClick={openPortal} disabled={portalLoading}
              className="mt-5 px-5 py-2.5 border border-gray-700 text-gray-300 hover:border-gray-500 hover:text-white text-sm font-medium rounded-xl transition disabled:opacity-50">
              {portalLoading ? 'Opening…' : 'Manage Subscription'}
            </button>
          )}
        </div>

        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
          <h2 className="font-semibold mb-4 flex items-center gap-2"><span>🧾</span> Payment History</h2>
          {status?.recentPayments?.length > 0 ? (
            <div className="space-y-2">{status.recentPayments.map(p => (
              <div key={p._id} className="flex items-center justify-between py-2 border-b border-gray-800 last:border-0">
                <div><p className="text-sm text-white capitalize">{p.planType} plan</p><p className="text-xs text-gray-500">{new Date(p.createdAt).toLocaleDateString()}</p></div>
                <span className="text-yellow-400 font-bold">${((p.amount || 0) / 100).toFixed(2)}</span>
              </div>
            ))}</div>
          ) : <p className="text-gray-500 text-sm">No payments yet</p>}
        </div>
        <div className="text-center text-xs text-gray-600 pb-4">🔒 Payments secured by Stripe. Card details never stored on Starfuel servers.</div>
      </main>
    </div>
  );
}
